/**
 * useUndoRedo.js — Section 6
 *
 * Array of state snapshots, max 50 entries.
 * Uses structuredClone() for deep copying (NOT JSON.parse/stringify).
 *
 * Undoable actions:
 *   add/remove track, add/remove/move/slice/duplicate/paste clip,
 *   change volume/pan/mute/solo, add/remove section marker, auto arrange
 *
 * Non-undoable (session-level):
 *   BPM change, bars change, project name change, export
 */

import { useState, useCallback, useRef } from 'react';

const MAX_SNAPSHOTS = 50;

/**
 * @param {Object} initialState - { tracks: [], sectionMarkers: [] }
 * @returns {Object} - { state, pushState, undo, redo, canUndo, canRedo }
 */
export function useUndoRedo(initialState) {
  // Current arrangement state
  const [state, setState] = useState(() => structuredClone(initialState));

  // Undo stack: past snapshots (before current state)
  const undoStack = useRef([]);

  // Redo stack: snapshots that were undone
  const redoStack = useRef([]);

  /**
   * Push a new state snapshot after an undoable operation.
   * This saves the CURRENT state to the undo stack, then sets new state.
   * Max 50 entries — drops oldest when exceeded.
   *
   * @param {Object} newState - The new arrangement state to apply
   */
  const pushState = useCallback((newState, previousMetadata = {}) => {
    setState((current) => {
      // Save current state to undo stack
      undoStack.current.push(structuredClone({ ...current, ...previousMetadata }));

      // Enforce max 50 snapshots — drop oldest
      if (undoStack.current.length > MAX_SNAPSHOTS) {
        undoStack.current.shift();
      }

      // Clear redo stack (new action invalidates redo history)
      redoStack.current = [];

      return structuredClone({ ...current, ...newState });
    });
  }, []);

  /**
   * Undo: pop from undo stack, push current to redo stack.
   */
  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;

    setState((current) => {
      // Push current state to redo stack
      redoStack.current.push(structuredClone(current));

      // Pop and restore previous state
      const previous = undoStack.current.pop();
      return previous;
    });
  }, []);

  /**
   * Redo: pop from redo stack, push current to undo stack.
   */
  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;

    setState((current) => {
      // Push current state to undo stack
      undoStack.current.push(structuredClone(current));

      // Pop and restore next state
      const next = redoStack.current.pop();
      return next;
    });
  }, []);

  const reset = useCallback((newState) => {
    undoStack.current = [];
    redoStack.current = [];
    setState(structuredClone(newState));
  }, []);

  // Persisted session metadata (for example playback progress) must not become an undo step.
  const updateState = useCallback((patchOrUpdater) => {
    setState((current) => {
      const apply = (snapshot) => (typeof patchOrUpdater === 'function'
        ? patchOrUpdater(snapshot) : { ...snapshot, ...patchOrUpdater });
      // Apply the metadata update to history too, so Undo cannot reset flags that only move forward.
      undoStack.current = undoStack.current.map((snapshot) => structuredClone(apply(snapshot)));
      redoStack.current = redoStack.current.map((snapshot) => structuredClone(apply(snapshot)));
      const next = apply(current);
      return structuredClone(next);
    });
  }, []);

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  return {
    state,
    pushState,
    undo,
    redo,
    reset,
    updateState,
    canUndo,
    canRedo,
  };
}

export default useUndoRedo;
